import { Link, Outlet, useParams } from 'react-router-dom';

import { useProject } from '@/hooks/use-projects';
import { ApiError } from '@/modules/api/api-client';

import { ProjectSidebar } from './project-sidebar';

/**
 * Layout wrapper for project pages.
 * Renders the project sub-navigation sidebar alongside the page content.
 *
 * Layout (within the main app layout):
 * +---------------------------------------------------+
 * � Main sidebar � Project sidebar �     Content      �
 * �  (w-16 icons)�  (w-16 icons)  �    (Outlet)      �
 * �              �  (brand dark)   �                  �
 * +---------------------------------------------------+
 */
export function ProjectLayout() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';

  const {
    isError: projectError,
    error: projectErrorObj,
  } = useProject(key);

  // Handle 403 - Access Denied
  if (projectError && projectErrorObj instanceof ApiError && projectErrorObj.status === 403) {
    return (
      <main id="main-content" className="flex-1 flex items-center justify-center p-6">
        <div className="text-center" role="alert">
          <h1 className="text-2xl font-bold text-content">Access Denied</h1>
          <p className="mt-2 text-content-secondary">
            You do not have permission to view this project.
          </p>
          <Link
            to="/projects"
            className="mt-4 inline-block rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Back to Projects
          </Link>
        </div>
      </main>
    );
  }

  // Handle 404 - Not Found
  if (projectError && projectErrorObj instanceof ApiError && projectErrorObj.status === 404) {
    return (
      <main id="main-content" className="flex-1 flex items-center justify-center p-6">
        <div className="text-center" role="alert">
          <h1 className="text-2xl font-bold text-content">Project Not Found</h1>
          <p className="mt-2 text-content-secondary">
            The project you are looking for does not exist.
          </p>
          <Link
            to="/projects"
            className="mt-4 inline-block rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Back to Projects
          </Link>
        </div>
      </main>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      <ProjectSidebar />
      <div className="flex-1 overflow-y-auto p-4">
        <Outlet />
      </div>
    </div>
  );
}
